/**
 * OrderStatusBadge — renders an order status as a colour-coded badge.
 */

import { Badge } from "@/ui/badge";
import { STATUS_MAP } from "../statusMap";

/**
 * Ba nhóm màu theo NGHĨA, không phải tám màu theo tên trạng thái (CEO chốt
 * 01/09/2026, phương án 4b): đang chạy = xanh dương, đã xong = xanh lá,
 * huỷ = đỏ, chưa chốt = viền xám. Bảng cũ ánh xạ tám trạng thái vào bốn kiểu
 * shadcn mặc định nên "Đã đặt lịch" và "Đã giao" ra cùng một viền trắng —
 * liếc qua không phân biệt được đơn mới nhận với đơn đã trả xong.
 *
 * Màu KHÔNG bao giờ đứng một mình: nhãn chữ luôn nằm cạnh (rule
 * `color-not-only`), nên người không phân biệt được màu vẫn đọc được.
 * Variant lấy từ token pastel trong ui/badge.tsx, không viết hex thô.
 */
type StatusVariant = "blue" | "green" | "red" | "outline";

const STATUS_VARIANT: Record<string, StatusVariant> = {
  // Chưa chốt — chỉ giữ chỗ lịch, chưa phải đơn thật.
  tentative: "outline",
  // Đang chạy trong quy trình.
  booked: "blue",
  contract: "blue",
  shooting: "blue",
  editing: "blue",
  // Đã xong việc.
  completed: "green",
  delivered: "green",
  // Dừng hẳn.
  cancelled: "red",
};

/**
 * Cặp nền/chữ cho từng nhóm. Chữ dựng từ token `--color-*-text` chứ KHÔNG từ
 * `--color-*` gốc: đo trên trang thật 01/09/2026, bộ pastel gốc chỉ cho chữ
 * 2,15-2,68 : 1 — dưới xa mức 4,5 : 1 và nhìn bợt hẳn đi, đúng cái làm thẻ
 * khó nhận ra.
 *
 * Vì sao còn phải đậm thêm 10%: bộ `-text` được chỉnh cho nền TRẮNG (chú thích
 * trong tokens.css ghi 5,21 / 5,09 / 5,71), nhưng thẻ nằm trên nền kem
 * #f5f0e8 nên nền tối hơn trắng, tương phản tụt còn 4,27 / 4,11 / 4,59 — hai
 * nhóm xanh vẫn trượt. Pha 90% token với đen kéo lên 5,03 / 4,85 / 5,37, dư
 * ngưỡng mà vẫn còn rõ màu. KHÔNG viết hex thô: gốc vẫn là token, ai đổi màu
 * thương hiệu thì thẻ đi theo.
 *
 * (Ứng dụng chỉ có nền sáng — tokens.css không có khối chế độ tối — nên pha
 * với đen luôn là đậm thêm, không có chiều ngược.)
 *
 * Đặt bằng `style` chứ không bằng lớp: `variant="outline"` của Badge đã mang
 * sẵn `text-foreground`, hai lớp cùng nhắm `color` thì thứ tự trong tệp CSS
 * quyết định — không chắc chắn. Inline style thì luôn thắng.
 */
function chuDam(token: string): string {
  return `color-mix(in srgb, var(${token}) 90%, #000)`;
}

const TONE_STYLE: Record<StatusVariant, { backgroundColor: string; color: string }> = {
  blue: { backgroundColor: "var(--color-info-bg)", color: chuDam("--color-info-text") },
  green: { backgroundColor: "var(--color-success-bg)", color: chuDam("--color-success-text") },
  red: { backgroundColor: "var(--color-danger-bg)", color: chuDam("--color-danger-text") },
  // Chưa chốt: để trống ruột, chỉ có viền — nhạt hơn hẳn ba nhóm kia.
  // Đo được 5,06 : 1, đã đạt, nên giữ nguyên token.
  outline: { backgroundColor: "transparent", color: "var(--color-text-secondary)" },
};

interface Props {
  status: string;
}

export function OrderStatusBadge({ status }: Props) {
  const label = STATUS_MAP[status] ?? status;
  const tone = STATUS_VARIANT[status] ?? "outline";
  return (
    <Badge variant="outline" style={TONE_STYLE[tone]}>
      {label}
    </Badge>
  );
}
